// public/js/user/userController.js
import BaseController from '@base/baseController.js';
import api from '@services/api.js';
import overlay from '@services/overlay.js';
import config from '@/config.js';
import userManager from "./userManager.js";
import userViews from "./userViews.js";

class UserController extends BaseController {
    constructor() {
        super({
            manager: userManager,
            views: userViews,
            api: api,
            overlay: overlay,
            entityName: 'user',
            entityNamePlural: 'users'
        });

        this.file_field = null;
        this.currentUserId = null;
    }

    // ===============================
    // SCHEMA-DRIVEN CONFIGURATION
    // ===============================

    /**
     * Override to customize which fields are editable for users
     * @returns {Array} Array of editable field names
     */
    getEditableFields() {
        // Users can edit their basic information including gender
        return ['firstName', 'lastName', 'gender', 'email'];
    }

    // ===============================
    // DATA PREPARATION
    // ===============================

    /**
     * Prepare profile image data for user edit form
     * @param {string} userId - User ID
     * @returns {Object} Profile image section data
     */
    async prepareProfileImageData(userId) {
        this.logger.debug(`Preparing profile image data for user ${userId}`);

        try {
            // Use parent.fileController to avoid circular dependencies
            if (!this.parent || !this.parent.fileController) {
                this.logger.warn('FileController not available via parent');
                return null;
            }

            const fileController = this.parent.fileController;

            // Fetch existing profile images for this user using dedicated endpoint
            // Note: Fixed to use camelCase 'relationshipType' instead of 'relationship_type'
            const existingImages = await fileController.getSubjectFiles('user', userId, {
                relationshipType: 'profile_image'  // ✓ FIXED: Backend expects camelCase
            });

            this.logger.debug(`Found ${existingImages?.length || 0} profile images for user ${userId}`);

            // Create file upload field using createEntityCard (which wraps createFileField)
            const uploadField = await fileController.createEntityCard({
                multiple: false,
                accept: 'image/*',
                maxSize: 10, // 10 MB
                autoUpload: true,
                preventOverlayClose: true, // Keep overlay open after upload for profile images
                relationship: {
                    subject_type: 'user',
                    subject_id: userId,
                    relationship_type: 'profile_image',
                    metadata: {
                        description: 'User profile image',
                        is_primary: false // New uploads are NOT primary by default
                    }
                },
                onUploadSuccess: async (uploadResults) => {
                    // Refresh the gallery after upload
                    this.logger.debug('Profile image uploaded, refreshing gallery');
                    await this.refreshProfileImageGallery(userId);
                }
            });

            // Find the primary profile image (marked with is_primary: true)
            // Note: metadata might be a JSON string or already parsed object
            const currentProfileImage = existingImages.find(img => {
                try {
                    const metadata = typeof img.relationship_metadata === 'string'
                        ? JSON.parse(img.relationship_metadata)
                        : img.relationship_metadata;
                    return metadata?.is_primary === true;
                } catch (e) {
                    return false;
                }
            }) || existingImages[0]; // Fallback to first image if no primary is set

            return {
                userId: userId,
                existingImages: existingImages || [],
                uploadField: uploadField,
                currentProfileImage: currentProfileImage
            };
        } catch (error) {
            this.logger.error('Error preparing profile image data:', error);
            return null;
        }
    }

    /**
     * Override prepareFormData to inject profile image section
     * PATTERN: Prepare → Super → Post-process
     * Note: Made async because we need to fetch profile image data
     */
    async prepareFormData(entity, isNew = false) {
        // 1. PREPARE: Fetch profile image data (only for existing users)
        let profileImageData = null;
        if (!isNew && entity.id) {
            profileImageData = await this.prepareProfileImageData(entity.id);
        }

        // 2. SUPER: Get base form configuration from BaseController (sync)
        const baseFormData = super.prepareFormData(entity, isNew);

        // 3. POST-PROCESS: Inject profile image section data and override title
        if (profileImageData) {
            baseFormData.profileImageSection = profileImageData;
        }

        // Override title for user forms
        if (!isNew) {
            baseFormData.title = 'Edit Your Profile';
        }

        return baseFormData;
    }

    /**
     * Refresh profile image gallery after new upload
     * @param {string} userId - User ID
     */
    async refreshProfileImageGallery(userId) {
        try {
            // Fetch updated list of images
            const fileController = this.parent.fileController;
            const updatedImages = await fileController.getSubjectFiles('user', userId, {
                relationshipType: 'profile_image'  // ✓ FIXED: Backend expects camelCase
            });

            this.logger.debug(`Refreshed gallery with ${updatedImages?.length || 0} profile images`);

            // Find the gallery container
            const gallery = document.querySelector('.profile-image-gallery');
            if (!gallery) {
                this.logger.warn('Profile image gallery not found');
                return;
            }

            // Clear and rebuild gallery
            gallery.innerHTML = '';

            // Find current primary image
            const currentProfileImage = updatedImages.find(img => {
                try {
                    const metadata = typeof img.relationship_metadata === 'string'
                        ? JSON.parse(img.relationship_metadata)
                        : img.relationship_metadata;
                    return metadata?.is_primary === true;
                } catch (e) {
                    return false;
                }
            }) || updatedImages[0];

            // Get profile image data for thumbnail creation
            const profileImageData = {
                userId: userId,
                existingImages: updatedImages,
                currentProfileImage: currentProfileImage
            };

            // Rebuild thumbnails
            updatedImages.forEach((image, index) => {
                if (index === 0) {
                    console.log('Sample user image object structure:', image);
                    console.log('Image ID:', image.id);
                    console.log('Image object_id:', image.object_id);
                }
                const thumbnail = this.views.createProfileImageThumbnail(image, profileImageData);
                gallery.appendChild(thumbnail);
            });

            // Update hidden input with current primary image
            const hiddenInput = document.getElementById('user-selected-profile-image-id');
            if (hiddenInput && currentProfileImage) {
                hiddenInput.value = currentProfileImage.id;
            }

            this.logger.success(`Gallery refreshed with ${updatedImages.length} images`);

        } catch (error) {
            this.logger.error('Error refreshing profile image gallery:', error);
        }
    }


    /**
     * Get current profile image ID for a user
     * @param {string} userId - User ID
     * @returns {string|null} Current profile image ID
     */
    async getCurrentProfileImageId(userId) {
        try {
            const response = await this.api.apiCall(`/api/users/${userId}/profile-image`, "GET");
            return response.data?.fileId || null;
        } catch (error) {
            this.logger.warn('Could not get current profile image:', error);
            return null;
        }
    }

    /**
     * Update user profile image
     * @param {string} userId - User ID
     * @param {string} fileId - New profile image file ID
     */
    async updateProfileImage(userId, fileId) {
        try {
            this.logger.debug(`Updating profile image for user ${userId} to file ${fileId}`);
            console.log('Frontend sending fileId:', fileId);

            const response = await this.api.apiCall(`/api/users/${userId}/profile-image`, "PUT", {
                fileId: fileId
            });

            if (response.status === 200) {
                this.logger.success('Profile image updated successfully');

                // Update user's photo_url in manager
                const user = this.manager.read(userId);
                if (user) {
                    // Trigger reload of profile image
                    user.photo_url = null;
                    await this.postProcessEntity(user);
                }
            }
        } catch (error) {
            this.logger.error('Error updating profile image:', error);
            this.showError('Failed to update profile image');
        }
    }

    // ===============================
    // USER-SPECIFIC API METHODS
    // ===============================

    async editImage() {
        this.logger.debug('Opening user image editor');
        try {
            const imageEditor = this.views.createUserImageEditor(this.file_field);
            await this.overlay.showOverlay(imageEditor);
        } catch (error) {
            this.errorHandler('editImage', error);
        }
    }

    /**
     * Get current user profile
     * @returns {Object} User profile data
     */
    async getCurrentUserProfile() {
        try {
            this.setLoadingState('getCurrentProfile', true);
            
            this.logger.api('Fetching current user profile');
            const data = await this.api.apiCall('/api/users/profile', "GET");
            
            const user = data.data || {};
            this.logger.api('Current user profile fetched successfully');
            return user;
        } catch (error) {
            this.logger.error('Failed to fetch current user profile:', error);
            this.errorHandler('getCurrentProfile', error);
            return null;
        } finally {
            this.setLoadingState('getCurrentProfile', false);
        }
    }

    /**
     * Update current user profile
     * @param {Object} updateData - Update data
     * @returns {Object} Updated user data
     */
    async updateCurrentUserProfile(updateData) {
        try {
            this.setLoadingState('updateProfile', true);
            
            this.logger.api('Updating current user profile');
            const response = await this.api.apiCall('/api/users/profile', "PUT", updateData);
            
            this.logger.success('User profile updated successfully');
            return response.data;
        } catch (error) {
            this.logger.error('Failed to update user profile:', error);
            this.handleAPIError('updateProfile', error, updateData);
            return null;
        } finally {
            this.setLoadingState('updateProfile', false);
        }
    }

    /**
     * Get users related through shared dogs
     * @returns {Array} Array of related users
     */
    async fetchRelatedUsers() {
        try {
            this.setLoadingState('fetchRelated', true);
            
            this.logger.api('Fetching related users (shared dogs)');
            const data = await this.api.apiCall('/api/users/related/shared-dogs', "GET");
            
            const users = data.data || [];
            this.logger.api(`Fetched ${users.length} related users`);
            return users;
        } catch (error) {
            this.logger.error('Failed to fetch related users:', error);
            this.errorHandler('fetchRelated', error);
            return [];
        } finally {
            this.setLoadingState('fetchRelated', false);
        }
    }

    /**
     * Search users with criteria
     * @param {Object} criteria - Search criteria
     * @returns {Array} Array of matching users
     */
    async searchUsers(criteria) {
        try {
            this.setLoadingState('searchUsers', true);
            
            const queryParams = new URLSearchParams(criteria).toString();
            const endpoint = `/api/users/search/users${queryParams ? `?${queryParams}` : ''}`;
            
            this.logger.api(`Searching users with criteria:`, criteria);
            const data = await this.api.apiCall(endpoint, "GET");
            
            const users = data.data || [];
            this.logger.api(`Found ${users.length} matching users`);
            return users;
        } catch (error) {
            this.logger.error('Failed to search users:', error);
            this.errorHandler('searchUsers', error);
            return [];
        } finally {
            this.setLoadingState('searchUsers', false);
        }
    }

    // ===============================
    // USER-SPECIFIC LOADING METHODS
    // ===============================

    /**
     * Load current user profile with caching
     * @returns {Object} User object
     */
    async loadCurrentUserProfile() {
        this.logger.process('Loading current user profile');
        // Get current user profile from API
        const userData = await this.getCurrentUserProfile();
        
        if (userData) {
            const userObject = this.manager.create(userData);
            
            // Cache current user ID
            this.currentUserId = userObject.id;
            
            // Load profile image if not already loaded
            if (!userObject.photo_url) {
                this.logger.debug(`User found but no image, loading now...`);
                await this.postProcessEntity(userObject);
            }
            
            this.logger.success(`Loaded current user profile: ${this.manager.getDisplayName(userObject)}`);
            return userObject;
        } else {
            this.logger.warn('Current user profile not found');
            return null;
        }
    }

    /**
     * Load related users with caching
     * @returns {Array} Array of user objects
     */
    async loadRelatedUsers() {
        this.logger.process('Loading related users');
        
        const users = await this.fetchRelatedUsers();
        
        if (users.length > 0) {
            // Create user objects but don't store them permanently in manager
            // since these are just basic details for display
            const userObjects = users.map(userData => this.manager.create(userData));
            this.logger.success(`Loaded ${userObjects.length} related users`);
            return userObjects;
        } else {
            this.logger.info('No related users found');
            return [];
        }
    }

    // ===============================
    // USER-SPECIFIC POST-PROCESSING
    // ===============================

    /**
     * Override postProcessEntity to handle user-specific image loading
     */
    async postProcessEntity(user) {
        this.logger.debug(`Post-processing user ${user.id} for images...`);
        
        if (!user.photo_url) {
            this.logger.debug(`Loading profile image for ${user.firstName || user.id}...`);
            
            const profileImageUrl = await this.getUserProfileImage(user.id);
            if (profileImageUrl) {
                user.photo_url = profileImageUrl;
                this.manager.update(user);
                this.logger.success(`Image loaded for ${user.firstName || user.id}`);
            } else {
                this.logger.debug(`No image found for ${user.firstName || user.id}`);
            }
        } else {
            this.logger.success(`Image already loaded for ${user.firstName || user.id}`);
        }
    }

    /**
     * Override postProcessEntities to handle bulk image loading
     */
    async postProcessEntities(users) {
        this.logger.debug(`Post-processing ${users.length} users for images...`);
        
        const imagePromises = users.map(user => this.postProcessEntity(user));
        await Promise.all(imagePromises);
        
        this.logger.success(`Finished processing ${users.length} users`);
    }

    // ===============================
    // USER-SPECIFIC DISPLAY METHODS
    // ===============================

    /**
     * Display current user profile (calls standard displayEntityDetails)
     * @param {any} file_field - Legacy parameter (unused)
     * @returns {HTMLElement} User details element
     */
    async displayCurrentUserProfile(file_field = null) {
        this.logger.ui('Displaying current user profile (via displayEntityDetails)');

        try {
            const user = await this.loadCurrentUserProfile();
            if (user) {
                // Use standard displayEntityDetails method
                return await this.displayEntityDetails(user.id);
            } else {
                const errorDiv = document.createElement('div');
                errorDiv.className = 'error-container';
                errorDiv.innerHTML = `
                    <h1>Profile not found</h1>
                    <p>Unable to load your profile. Please try logging in again.</p>
                `;
                return errorDiv;
            }
        } catch (error) {
            this.logger.error('Error displaying user profile:', error);
            this.errorHandler('displayProfile', error);
        }
    }

    /**
     * Display related users
     */
    async displayRelatedUsers() {
        this.logger.ui('Displaying related users');
        const content_container = document.getElementById("content-container");

        try {
            const container = this.views.createUserContainer();
            content_container.appendChild(container);

            const user_cards_container = document.getElementById("user-cards-container");
            user_cards_container.innerHTML = "";

            const relatedUsers = await this.loadRelatedUsers();
            
            for (const user of relatedUsers) {
                const cardData = this.prepareCardData(user);
                const userCard = this.views.createUserCard(cardData);
                user_cards_container.appendChild(userCard);
            }

            if (relatedUsers.length === 0) {
                user_cards_container.innerHTML = `
                    <div class="no-results">
                        <p>No related users found. Related users are people who share dogs with you.</p>
                    </div>
                `;
            }

            this.logger.success('Related users displayed successfully');

        } catch (error) {
            this.logger.error('Error displaying related users:', error);
            this.errorHandler('displayRelated', error);
        }
    }

    // ===============================
    // OVERRIDE ENTITY CRUD FOR USERS
    // ===============================

    /**
     * Override handleEditEntity for user profile editing
     * @param {string} entityId - Entity ID from button data attribute
     */
    async handleEditEntity(entityId) {
        try {
            // For users, we only allow editing current user profile
            if (entityId) {
                this.current_entity_id = entityId;
            }
            
            this.logger.process(`Handling edit user profile: ${this.current_entity_id}`);
            
            const currentUser = this.manager.read(this.current_entity_id);
            if (!currentUser) {
                // Try to load current profile if not in cache
                const loadedUser = await this.loadCurrentUserProfile();
                if (!loadedUser) {
                    this.showError('User profile not found.');
                    return;
                }
            }
            
            const userToEdit = this.manager.read(this.current_entity_id);
            const formData = await this.prepareFormData(userToEdit, false);
            const editView = this.views.createEntityEditView(formData);
            await this.overlay.showOverlay(editView);
            
            this.logger.success('User profile edit form displayed');
        } catch (error) {
            this.logger.error('Failed to handle edit user profile:', error);
            this.errorHandler('edit', error);
        }
    }

    /**
     * Override handleSaveEntity for user profile updates
     * User uses custom API endpoint instead of standard updateEntityAPI
     * PATTERN: Prepare → Process → Post-process (with profile image handling)
     */
    async handleSaveEntity() {
        try {
            this.logger.process(`Handling save user profile: ${this.current_entity_id}`);

            // 1. PREPARE: Check if profile image was changed
            const selectedProfileImageId = document.getElementById('user-selected-profile-image-id')?.value;
            const originalProfileImageId = await this.getCurrentProfileImageId(this.current_entity_id);
            const profileImageChanged = selectedProfileImageId && selectedProfileImageId !== originalProfileImageId;

            this.logger.debug(`Profile image check - Selected: ${selectedProfileImageId}, Original: ${originalProfileImageId}, Changed: ${profileImageChanged}`);

            const formData = this.collectFormData();

            // Validate form data using base helper
            if (!this.validateFormData(formData)) {
                return; // Validation failed, errors already shown
            }

            // 2. PROCESS: Use the special profile update endpoint
            const updatedUser = await this.updateCurrentUserProfile(formData);

            if (updatedUser) {
                this.manager.update(updatedUser);

                // 3. POST-PROCESS: Update profile image if changed
                if (profileImageChanged) {
                    this.logger.debug(`Profile image changed from ${originalProfileImageId} to ${selectedProfileImageId}`);
                    await this.updateProfileImage(this.current_entity_id, selectedProfileImageId);
                }

                this.overlay.closeOverlay();

                // Replace just the user details element (not the entire page)
                const existingDetails = document.getElementById(`user-details-${this.current_entity_id}`);
                if (existingDetails) {
                    const updatedUserDetails = await this.displayEntityDetails(this.current_entity_id);
                    existingDetails.replaceWith(updatedUserDetails);
                    this.logger.success('User details element replaced with updated data');
                }

                // Update navigation bar profile image
                await this.updateNavigationUserImage();

                this.showSuccess('Profile updated successfully');
            }
        } catch (error) {
            this.logger.error('Failed to handle save user profile:', error);
            this.errorHandler('save', error);
        }
    }

    /**
     * User controller uses base collectFormData() - gender is already in editable fields
     * No custom form data needed, base implementation handles everything
     */

    /**
     * Override handleDeleteEntity - Users cannot delete themselves
     */
    async handleDeleteEntity() {
        this.showError('You cannot delete your own user account. Please contact an administrator.');
    }

    /**
     * Override handleCreateEntity - Users cannot create new users
     */
    async handleCreateEntity() {
        this.showError('User creation is handled through the registration process.');
    }

    /**
     * Override handleCreateNewEntity - Users cannot create new users
     */
    async handleCreateNewEntity() {
        this.showError('User creation is handled through the registration process.');
    }

    // ===============================
    // DATA PREPARATION (Use Manager Methods)
    // ===============================

    prepareCardData(user) {
        return this.manager.prepareCardData(user);
    }

    /**
     * Override prepareDetailsData to add isCurrentUser flag and password change action
     * PATTERN: Prepare → Super → Post-process
     */
    prepareDetailsData(user) {
        const detailsData = this.manager.prepareDetailsData(user);

        // Add isCurrentUser flag (used by views for Edit Profile button)
        detailsData.isCurrentUser = (user.id === this.current_entity_id);

        // Append password change action to existing actions
        if (!detailsData.actions) {
            detailsData.actions = [];
        }

        detailsData.actions.push({
            name: 'change_password',
            icon: 'lock_reset',
            title: 'Change Password',
            entityId: user.id
        });

        return detailsData;
    }


    // ===============================
    // USER-SPECIFIC IMAGE HANDLING
    // ===============================

    /**
     * Get user profile image URL
     * @param {string} userId - User ID
     * @returns {string|null} Image URL or null
     */
    async getUserProfileImage(userId) {
        try {
            this.logger.debug(`Fetching profile image for user ${userId}...`);

            const response = await this.api.apiCall(`/api/users/${userId}/profile-image`, "GET");

            if (response.status === 200 && response.data && response.data.fileId) {
                this.logger.debug(`Found file ID ${response.data.fileId} for user ${userId}`);

                const imageUrl = `/api/files/${response.data.fileId}/view`;

                const imageBlob = await this.api.apiCall(
                    imageUrl,
                    "GET",
                    null,
                    false,
                    true
                );

                const blobUrl = URL.createObjectURL(imageBlob.data || imageBlob);

                this.logger.success(`Image loaded successfully for user ${userId}`);
                return blobUrl;
            } else {
                this.logger.debug(`No profile image found for user ${userId}`);
                return null;
            }
        } catch (error) {
            // 404 is expected when user has no profile image, don't log as error
            if (error.status === 404) {
                this.logger.debug(`No profile image found for user ${userId}`);
                return null;
            }
            // Log other errors as actual errors
            this.logger.error(`Error fetching profile image for user ${userId}:`, error);
            return null;
        }
    }

    /**
     * Update navigation user image immediately
     */
    async updateNavigationUserImage() {
        try {
            this.logger.debug('Updating navigation user image...');
            
            // Get current user if not cached
            let currentUser = null;
            if (!this.currentUserId) {
                currentUser = await this.loadCurrentUserProfile();
                if (!currentUser) return;
            } else {
                currentUser = this.manager.read(this.currentUserId);
                if (!currentUser) {
                    currentUser = await this.loadCurrentUserProfile();
                    if (!currentUser) return;
                }
            }
            
            // Get updated image URL (actual image or gender-based placeholder)
            const imageUrl = currentUser.photo_url || this.manager.getGenderBasedPlaceholder(currentUser);
            
            // Update navigation buttons
            const userImageButton = document.getElementById('user_image');
            const personButton = document.getElementById('person');
            
            if (imageUrl) {
                // Create or update image elements
                if (userImageButton) {
                    this.updateNavigationButton(userImageButton, imageUrl, !currentUser.photo_url);
                }
                if (personButton) {
                    this.updateNavigationButton(personButton, imageUrl, !currentUser.photo_url);
                }
                
                this.logger.success('Navigation user image updated');
            } else {
                // Reset to default icon (for other/prefer_not_to_say gender)
                if (userImageButton) {
                    this.resetNavigationButton(userImageButton);
                }
                if (personButton) {
                    this.resetNavigationButton(personButton);
                }
                
                this.logger.debug('Navigation user image reset to default');
            }
        } catch (error) {
            this.logger.error('Error updating navigation user image:', error);
        }
    }

    /**
     * Update navigation button with user image
     * @param {HTMLElement} button - Navigation button element
     * @param {string} imageUrl - Image URL
     * @param {boolean} isPlaceholder - Whether this is a gender-based placeholder
     */
    updateNavigationButton(button, imageUrl, isPlaceholder = false) {
        // Remove existing content
        button.innerHTML = '';
        
        // Remove material-symbols-outlined class and add profile image class
        button.classList.remove('material-symbols-outlined');
        button.classList.add('user-profile-image-nav');
        
        // Create image element
        const img = document.createElement('img');
        img.src = imageUrl;
        img.className = isPlaceholder ? 'nav-user-placeholder' : 'nav-user-image';
        img.alt = 'Profile Image';
        
        // Add error handler for gender-based placeholders
        if (isPlaceholder) {
            img.onerror = () => {
                // If gender-based image fails to load, reset to Material icon
                this.resetNavigationButton(button);
            };
        }
        
        button.appendChild(img);
    }

    /**
     * Reset navigation button to default icon
     * @param {HTMLElement} button - Navigation button element
     */
    resetNavigationButton(button) {
        // Remove image-related classes
        button.classList.remove('user-profile-image-nav');
        button.classList.add('material-symbols-outlined');
        
        // Reset to default icon
        button.innerHTML = 'person';
    }

    // ===============================
    // GENDER-SPECIFIC METHODS
    // ===============================

    /**
     * Update user gender specifically
     * @param {string} gender - Gender value
     * @returns {boolean} Success status
     */
    async updateUserGender(gender) {
        try {
            this.logger.process(`Updating user gender to: ${gender}`);
            
            // Validate gender value
            const validGenders = ['male', 'female', 'other', 'prefer_not_to_say'];
            if (!validGenders.includes(gender)) {
                this.showError('Invalid gender value.');
                return false;
            }
            
            const updateData = { gender };
            const updatedUser = await this.updateCurrentUserProfile(updateData);
            
            if (updatedUser) {
                this.manager.update(updatedUser);
                this.showSuccess('Gender updated successfully');
                return true;
            }
            
            return false;
        } catch (error) {
            this.logger.error('Failed to update user gender:', error);
            this.errorHandler('updateGender', error);
            return false;
        }
    }

    /**
     * Get users by gender (for admin/analysis purposes)
     * @param {string} gender - Gender to filter by
     * @returns {Array} Array of users with specified gender
     */
    getUsersByGender(gender) {
        return this.manager.getUsersByGender(gender);
    }

    /**
     * Get gender statistics from loaded users
     * @returns {Object} Gender statistics
     */
    getGenderStatistics() {
        const users = this.manager.read();
        const stats = {
            total: users.length,
            male: users.filter(u => u.gender === 'male').length,
            female: users.filter(u => u.gender === 'female').length,
            other: users.filter(u => u.gender === 'other').length,
            prefer_not_to_say: users.filter(u => u.gender === 'prefer_not_to_say' || !u.gender).length
        };
        
        // Calculate percentages
        if (stats.total > 0) {
            stats.percentages = {
                male: Math.round((stats.male / stats.total) * 100),
                female: Math.round((stats.female / stats.total) * 100),
                other: Math.round((stats.other / stats.total) * 100),
                prefer_not_to_say: Math.round((stats.prefer_not_to_say / stats.total) * 100)
            };
        }
        
        return stats;
    }

    // ===============================
    // DEBUG METHODS (as requested)
    // ===============================

    /**
     * Debug function: Print all loaded users from manager to console
     */
    debugPrintAllUsers() {
        this.logger.debug('Printing all users from manager...');
        this.manager.debugPrintAllUsers();
    }

    /**
     * API endpoint to trigger debug print
     */
    async triggerDebugPrint() {
        try {
            const response = await this.api.apiCall('/api/users/debug/print-all', "GET");
            this.logger.debug('Server debug print triggered:', response.message);
            
            // Also print from local manager
            this.debugPrintAllUsers();
            
            return response;
        } catch (error) {
            this.logger.error('Failed to trigger debug print:', error);
            return null;
        }
    }

    // ===============================
    // CUSTOM ACTION HANDLERS
    // ===============================

    /**
     * Override actionHandlers to add password change action
     * @returns {Object} Map of action names to handler functions
     */
    get actionHandlers() {
        return {
            ...super.actionHandlers,  // Keep all base handlers (edit, delete, etc.)
            'change_password': () => this.handlePasswordChange()  // Add custom password change handler
        };
    }

    /**
     * Handle password change action - opens auth module's password change overlay
     */
    async handlePasswordChange() {
        try {
            this.logger.debug('Opening password change overlay');

            // Dynamically import auth controller to avoid circular dependencies
            const { default: authController } = await import('../auth/authController.js');

            // Use auth module's password change overlay
            authController.showChangePasswordOverlay();

            this.logger.success('Password change overlay opened');
        } catch (error) {
            this.logger.error('Failed to open password change overlay:', error);
            this.showError('Failed to open password change form. Please try again.');
        }
    }

    // ===============================
    // INITIALIZATION METHOD
    // ===============================

    /**
     * Initialize user controller and load current user image
     */
    async init() {
        try {
            // Load current user profile and update navigation
            await this.loadCurrentUserProfile();
            await this.updateNavigationUserImage();
        } catch (error) {
            this.logger.error('Error initializing user controller:', error);
        }
    }

    // ===============================
    // ADMIN USER MANAGEMENT
    // ===============================

    /**
     * Display user management dashboard for admins
     */
    async displayUserManagement() {
        this.logger.ui('Displaying user management dashboard');
        const contentContainer = document.getElementById("content-container");

        if (!contentContainer) {
            this.logger.error('Content container not found');
            return;
        }

        // Clear existing content
        contentContainer.innerHTML = '';

        // Create admin panel structure
        const adminPanel = document.createElement('div');
        adminPanel.className = 'admin-panel';
        adminPanel.innerHTML = `
            <div class="admin-header">
                <h1>
                    <span class="material-symbols-outlined">group</span>
                    User Management
                </h1>
                <p class="admin-subtitle">Manage user accounts, roles, and permissions</p>
            </div>

            <div class="user-management-container">
                <div class="user-actions-bar">
                    <button id="btn-add-user" class="btn-primary">
                        <span class="material-symbols-outlined">person_add</span>
                        Add User
                    </button>
                    <button id="btn-refresh-users" class="btn-secondary">
                        <span class="material-symbols-outlined">refresh</span>
                        Refresh
                    </button>
                </div>

                <div class="user-table-container">
                    <table class="user-table">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Email</th>
                                <th>Role</th>
                                <th>Status</th>
                                <th>Gender</th>
                                <th>Created</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody id="users-table-body">
                            <tr>
                                <td colspan="7" class="loading-cell">Loading users...</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        `;

        contentContainer.appendChild(adminPanel);

        // Setup event listeners
        this.setupUserManagementListeners();

        // Load users
        await this.loadUsersTable();
    }

    /**
     * Setup event listeners for user management
     */
    setupUserManagementListeners() {
        // Add user button
        const addBtn = document.getElementById('btn-add-user');
        if (addBtn) {
            addBtn.addEventListener('click', () => this.handleAddUser());
        }

        // Refresh button
        const refreshBtn = document.getElementById('btn-refresh-users');
        if (refreshBtn) {
            refreshBtn.addEventListener('click', () => this.loadUsersTable());
        }
    }

    /**
     * Load users and populate table
     */
    async loadUsersTable() {
        try {
            this.logger.debug('Loading users table...');

            const tbody = document.getElementById('users-table-body');
            if (!tbody) return;

            // Show loading state
            tbody.innerHTML = '<tr><td colspan="7" class="loading-cell">Loading users...</td></tr>';

            // Fetch users from API
            const response = await this.api.get(`${config.apiUrl}/users`);
            const users = response.data || [];

            this.logger.debug(`Loaded ${users.length} users`);

            // DEBUG LOG - Check for locked users in API response
            const lockedUsers = users.filter(u => u.isLocked);
            if (lockedUsers.length > 0) {
                console.log(`[Frontend] Received ${lockedUsers.length} locked user(s) from API:`);
                lockedUsers.forEach(u => console.log(`  - ${u.email}: isLocked=${u.isLocked}, failedAttempts=${u.failedAttempts}`));
            } else {
                console.log('[Frontend] No locked users received from API');
            }

            // Clear table
            tbody.innerHTML = '';

            if (users.length === 0) {
                tbody.innerHTML = '<tr><td colspan="7" class="empty-cell">No users found</td></tr>';
                return;
            }

            // Populate table rows
            users.forEach(user => {
                const row = this.createUserTableRow(user);
                tbody.appendChild(row);
            });

            this.logger.success('Users table loaded successfully');

        } catch (error) {
            this.logger.error('Failed to load users:', error);
            const tbody = document.getElementById('users-table-body');
            if (tbody) {
                tbody.innerHTML = '<tr><td colspan="7" class="error-cell">Failed to load users. Please try again.</td></tr>';
            }
        }
    }

    /**
     * Create table row for user
     * @param {Object} user - User data
     * @returns {HTMLElement} Table row element
     */
    createUserTableRow(user) {
        // DEBUG LOG
        if (user.isLocked) {
            console.log(`[Frontend.createUserTableRow] Creating row for LOCKED user: ${user.email}, isLocked=${user.isLocked}, failedAttempts=${user.failedAttempts}`);
        }

        const row = document.createElement('tr');
        row.dataset.userId = user.id;

        const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'N/A';
        const createdDate = user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A';
        const genderDisplay = this.getGenderDisplayName(user.gender || 'prefer_not_to_say');

        // Role badge
        let roleDisplay = '';
        if (user.role === 'admin') {
            roleDisplay = '<span class="role-badge role-admin">Admin</span>';
        } else if (user.role === 'pro') {
            roleDisplay = '<span class="role-badge role-pro">Pro</span>';
        } else {
            roleDisplay = '<span class="role-badge role-user">User</span>';
        }

        // Status badges
        const emailBadge = user.emailVerified ?
            '<span class="status-badge status-verified" title="Email verified">✓ Verified</span>' :
            '<span class="status-badge status-unverified" title="Email not verified">✗ Not Verified</span>';

        const activatedBadge = user.adminActivated ?
            '<span class="status-badge status-active" title="Admin activated">✓ Active</span>' :
            '<span class="status-badge status-inactive" title="Not activated">✗ Inactive</span>';

        // Locked status badge (if user is locked)
        const lockedBadge = user.isLocked ?
            '<span class="status-badge status-locked" title="Account locked due to failed login attempts">🔒 Locked</span>' :
            '';

        row.innerHTML = `
            <td>${fullName}</td>
            <td>${user.email || 'N/A'}</td>
            <td>${roleDisplay}</td>
            <td><div class="status-badges">${emailBadge}${activatedBadge}${lockedBadge}</div></td>
            <td>${genderDisplay}</td>
            <td>${createdDate}</td>
            <td class="actions-cell">
                <button class="btn-icon btn-edit-user" data-user-id="${user.id}" title="Edit User">
                    <span class="material-symbols-outlined">edit</span>
                </button>
                <button class="btn-icon btn-delete-user" data-user-id="${user.id}" title="Delete User">
                    <span class="material-symbols-outlined">delete</span>
                </button>
                ${user.isLocked ? `
                <button class="btn-icon btn-unlock-user" data-user-id="${user.id}" title="Unlock Account">
                    <span class="material-symbols-outlined">lock_open</span>
                </button>
                ` : ''}
            </td>
        `;

        // Add event listeners to action buttons
        const editBtn = row.querySelector('.btn-edit-user');
        const deleteBtn = row.querySelector('.btn-delete-user');
        const unlockBtn = row.querySelector('.btn-unlock-user');

        if (editBtn) {
            editBtn.addEventListener('click', () => this.handleEditUser(user.id));
        }

        if (deleteBtn) {
            deleteBtn.addEventListener('click', () => this.handleDeleteUser(user.id));
        }

        if (unlockBtn) {
            unlockBtn.addEventListener('click', () => this.handleUnlockUser(user.id, user.email));
        }

        return row;
    }

    /**
     * Handle add user action
     */
    async handleAddUser() {
        this.logger.debug('Opening add user overlay');

        const content = document.createElement('div');
        content.className = 'user-overlay-content';
        content.innerHTML = `
            <div class="user-overlay-header">
                <h2>Add New User</h2>
                <button class="user-overlay-close" id="close-add-user">
                    <span class="material-symbols-outlined">close</span>
                </button>
            </div>
            <div class="user-overlay-body">
                <div class="form-group">
                    <label for="user-first-name">First Name *</label>
                    <input type="text" id="user-first-name" class="user-input" required>
                </div>
                <div class="form-group">
                    <label for="user-last-name">Last Name *</label>
                    <input type="text" id="user-last-name" class="user-input" required>
                </div>
                <div class="form-group">
                    <label for="user-email">Email *</label>
                    <input type="email" id="user-email" class="user-input" required>
                </div>
                <div class="form-group">
                    <label for="user-password">Password *</label>
                    <input type="password" id="user-password" class="user-input" required>
                </div>
                <div class="form-group">
                    <label for="user-role">Role *</label>
                    <select id="user-role" class="user-input">
                        <option value="user">User</option>
                        <option value="pro">Pro User</option>
                        <option value="admin">Admin</option>
                    </select>
                </div>
                <div class="form-group">
                    <label for="user-gender">Gender</label>
                    <select id="user-gender" class="user-input">
                        <option value="prefer_not_to_say">Prefer not to say</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                        <option value="other">Other</option>
                    </select>
                </div>
                <div class="form-group">
                    <label class="checkbox-label">
                        <input type="checkbox" id="user-email-verified" class="user-checkbox">
                        Email Verified
                    </label>
                </div>
                <div class="form-group">
                    <label class="checkbox-label">
                        <input type="checkbox" id="user-admin-activated" class="user-checkbox" checked>
                        Admin Activated
                    </label>
                </div>
                <div id="user-error" class="user-overlay-error" style="display: none;"></div>
            </div>
            <div class="user-overlay-footer">
                <button class="btn-secondary" id="cancel-add-user">Cancel</button>
                <button class="btn-primary" id="submit-add-user">
                    <span class="material-symbols-outlined">person_add</span>
                    Add User
                </button>
            </div>
        `;

        this.overlay.showOverlay(content);

        // Event listeners
        document.getElementById('close-add-user').addEventListener('click', () => this.overlay.closeOverlay());
        document.getElementById('cancel-add-user').addEventListener('click', () => this.overlay.closeOverlay());
        document.getElementById('submit-add-user').addEventListener('click', () => this.submitAddUser());
    }

    /**
     * Submit add user form
     */
    async submitAddUser() {
        const firstName = document.getElementById('user-first-name').value.trim();
        const lastName = document.getElementById('user-last-name').value.trim();
        const email = document.getElementById('user-email').value.trim();
        const password = document.getElementById('user-password').value;
        const role = document.getElementById('user-role').value;
        const gender = document.getElementById('user-gender').value;
        const emailVerified = document.getElementById('user-email-verified').checked;
        const adminActivated = document.getElementById('user-admin-activated').checked;
        const errorDiv = document.getElementById('user-error');

        // Validation
        if (!firstName || !lastName || !email || !password) {
            errorDiv.textContent = 'Please fill in all required fields';
            errorDiv.style.display = 'block';
            return;
        }

        if (password.length < 8) {
            errorDiv.textContent = 'Password must be at least 8 characters';
            errorDiv.style.display = 'block';
            return;
        }

        try {
            const submitBtn = document.getElementById('submit-add-user');
            submitBtn.disabled = true;
            submitBtn.innerHTML = '<span class="material-symbols-outlined">hourglass_empty</span> Creating...';

            // Unified user object with both app and auth data
            const userData = {
                firstName,
                lastName,
                email,
                password,
                role,
                gender,
                emailVerified,
                adminActivated
            };

            await this.api.post(`${config.apiUrl}/users`, userData);

            this.overlay.closeOverlay();
            this.showNotification('User created successfully', 'success');
            await this.loadUsersTable();

        } catch (error) {
            this.logger.error('Failed to create user:', error);
            errorDiv.textContent = error.message || 'Failed to create user';
            errorDiv.style.display = 'block';

            const submitBtn = document.getElementById('submit-add-user');
            submitBtn.disabled = false;
            submitBtn.innerHTML = '<span class="material-symbols-outlined">person_add</span> Add User';
        }
    }

    /**
     * Handle edit user action
     * @param {string} userId - User ID
     */
    async handleEditUser(userId) {
        this.logger.debug(`Opening edit user overlay for ${userId}`);

        try {
            // Fetch user data
            const response = await this.api.get(`${config.apiUrl}/users/${userId}`);
            const user = response.data;

            const content = document.createElement('div');
            content.className = 'user-overlay-content';
            content.innerHTML = `
                <div class="user-overlay-header">
                    <h2>Edit User</h2>
                    <button class="user-overlay-close" id="close-edit-user">
                        <span class="material-symbols-outlined">close</span>
                    </button>
                </div>
                <div class="user-overlay-body">
                    <div class="form-group">
                        <label for="edit-user-first-name">First Name *</label>
                        <input type="text" id="edit-user-first-name" class="user-input" value="${user.firstName || ''}" required>
                    </div>
                    <div class="form-group">
                        <label for="edit-user-last-name">Last Name *</label>
                        <input type="text" id="edit-user-last-name" class="user-input" value="${user.lastName || ''}" required>
                    </div>
                    <div class="form-group">
                        <label for="edit-user-email">Email *</label>
                        <input type="email" id="edit-user-email" class="user-input" value="${user.email || ''}" required>
                    </div>
                    <div class="form-group">
                        <label for="edit-user-role">Role *</label>
                        <select id="edit-user-role" class="user-input">
                            <option value="user" ${user.role === 'user' ? 'selected' : ''}>User</option>
                            <option value="pro" ${user.role === 'pro' ? 'selected' : ''}>Pro User</option>
                            <option value="admin" ${user.role === 'admin' ? 'selected' : ''}>Admin</option>
                        </select>
                    </div>
                    <div class="form-group">
                        <label for="edit-user-gender">Gender</label>
                        <select id="edit-user-gender" class="user-input">
                            <option value="prefer_not_to_say" ${user.gender === 'prefer_not_to_say' ? 'selected' : ''}>Prefer not to say</option>
                            <option value="male" ${user.gender === 'male' ? 'selected' : ''}>Male</option>
                            <option value="female" ${user.gender === 'female' ? 'selected' : ''}>Female</option>
                            <option value="other" ${user.gender === 'other' ? 'selected' : ''}>Other</option>
                        </select>
                    </div>
                    <div class="form-group">
                        <label class="checkbox-label">
                            <input type="checkbox" id="edit-user-email-verified" class="user-checkbox" ${user.emailVerified ? 'checked' : ''}>
                            Email Verified
                        </label>
                    </div>
                    <div class="form-group">
                        <label class="checkbox-label">
                            <input type="checkbox" id="edit-user-admin-activated" class="user-checkbox" ${user.adminActivated ? 'checked' : ''}>
                            Admin Activated
                        </label>
                    </div>
                    <div class="form-group password-reset-group">
                        <label>Password Management</label>
                        <p class="password-info">For security, passwords cannot be changed directly. Use the button below to send a password reset email.</p>
                        <button type="button" class="btn-warning" id="send-password-reset-btn">
                            <span class="material-symbols-outlined">mail</span>
                            Send Password Reset Email
                        </button>
                    </div>
                    <div id="edit-user-error" class="user-overlay-error" style="display: none;"></div>
                </div>
                <div class="user-overlay-footer">
                    <button class="btn-secondary" id="cancel-edit-user">Cancel</button>
                    <button class="btn-primary" id="submit-edit-user" data-user-id="${userId}">
                        <span class="material-symbols-outlined">save</span>
                        Save Changes
                    </button>
                </div>
            `;

            this.overlay.showOverlay(content);

            // Event listeners
            document.getElementById('close-edit-user').addEventListener('click', () => this.overlay.closeOverlay());
            document.getElementById('cancel-edit-user').addEventListener('click', () => this.overlay.closeOverlay());
            document.getElementById('submit-edit-user').addEventListener('click', () => this.submitEditUser(userId));
            document.getElementById('send-password-reset-btn').addEventListener('click', () => this.sendPasswordResetEmail(user.email, userId));

        } catch (error) {
            this.logger.error('Failed to load user for editing:', error);
            this.showNotification('Failed to load user data', 'error');
        }
    }

    /**
     * Submit edit user form
     * @param {string} userId - User ID
     */
    async submitEditUser(userId) {
        const firstName = document.getElementById('edit-user-first-name').value.trim();
        const lastName = document.getElementById('edit-user-last-name').value.trim();
        const email = document.getElementById('edit-user-email').value.trim();
        const role = document.getElementById('edit-user-role').value;
        const gender = document.getElementById('edit-user-gender').value;
        const emailVerified = document.getElementById('edit-user-email-verified').checked;
        const adminActivated = document.getElementById('edit-user-admin-activated').checked;
        const errorDiv = document.getElementById('edit-user-error');

        // Validation
        if (!firstName || !lastName || !email) {
            errorDiv.textContent = 'Please fill in all required fields';
            errorDiv.style.display = 'block';
            return;
        }

        try {
            const submitBtn = document.getElementById('submit-edit-user');
            submitBtn.disabled = true;
            submitBtn.innerHTML = '<span class="material-symbols-outlined">hourglass_empty</span> Saving...';

            // Unified user object with both app and auth data (no password field)
            const userData = {
                firstName,
                lastName,
                email,
                role,
                gender,
                emailVerified,
                adminActivated
            };

            await this.api.put(`${config.apiUrl}/users/${userId}`, userData);

            this.overlay.closeOverlay();
            this.showNotification('User updated successfully', 'success');
            await this.loadUsersTable();

        } catch (error) {
            this.logger.error('Failed to update user:', error);
            errorDiv.textContent = error.message || 'Failed to update user';
            errorDiv.style.display = 'block';

            const submitBtn = document.getElementById('submit-edit-user');
            submitBtn.disabled = false;
            submitBtn.innerHTML = '<span class="material-symbols-outlined">save</span> Save Changes';
        }
    }

    /**
     * Send password reset email to user
     * @param {string} email - User email
     * @param {string} userId - User ID for UI context
     */
    async sendPasswordResetEmail(email, userId) {
        this.logger.debug(`Sending password reset email to ${email}`);

        try {
            const resetBtn = document.getElementById('send-password-reset-btn');
            if (resetBtn) {
                resetBtn.disabled = true;
                resetBtn.innerHTML = '<span class="material-symbols-outlined">hourglass_empty</span> Sending...';
            }

            // Call the forgot-password endpoint
            await this.api.post(`${config.apiUrl}/auth/forgot-password`, { email });

            this.showNotification(`Password reset email sent to ${email}`, 'success');
            this.logger.success(`Password reset email sent to ${email}`);

            // Re-enable button after a delay
            setTimeout(() => {
                if (resetBtn) {
                    resetBtn.disabled = false;
                    resetBtn.innerHTML = '<span class="material-symbols-outlined">mail</span> Send Password Reset Email';
                }
            }, 3000);

        } catch (error) {
            this.logger.error('Failed to send password reset email:', error);
            this.showNotification('Failed to send password reset email', 'error');

            const resetBtn = document.getElementById('send-password-reset-btn');
            if (resetBtn) {
                resetBtn.disabled = false;
                resetBtn.innerHTML = '<span class="material-symbols-outlined">mail</span> Send Password Reset Email';
            }
        }
    }

    /**
     * Handle delete user action
     * @param {string} userId - User ID
     */
    async handleDeleteUser(userId) {
        this.logger.debug(`Opening delete confirmation for user ${userId}`);

        try {
            // Fetch user data for display
            const response = await this.api.get(`${config.apiUrl}/users/${userId}`);
            const user = response.data;
            const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim();

            const content = document.createElement('div');
            content.className = 'user-overlay-content';
            content.innerHTML = `
                <div class="user-overlay-header">
                    <h2>Delete User</h2>
                    <button class="user-overlay-close" id="close-delete-user">
                        <span class="material-symbols-outlined">close</span>
                    </button>
                </div>
                <div class="user-overlay-body">
                    <div class="user-delete-warning">
                        <span class="material-symbols-outlined">warning</span>
                        <div>
                            <p><strong>Are you sure you want to delete this user?</strong></p>
                            <p>User: <strong>${fullName}</strong> (${user.email})</p>
                            <p class="warning-text">This action cannot be undone. All user data will be permanently deleted.</p>
                        </div>
                    </div>
                </div>
                <div class="user-overlay-footer">
                    <button class="btn-secondary" id="cancel-delete-user">Cancel</button>
                    <button class="btn-danger" id="confirm-delete-user" data-user-id="${userId}">
                        <span class="material-symbols-outlined">delete</span>
                        Delete User
                    </button>
                </div>
            `;

            this.overlay.showOverlay(content);

            // Event listeners
            document.getElementById('close-delete-user').addEventListener('click', () => this.overlay.closeOverlay());
            document.getElementById('cancel-delete-user').addEventListener('click', () => this.overlay.closeOverlay());
            document.getElementById('confirm-delete-user').addEventListener('click', () => this.submitDeleteUser(userId));

        } catch (error) {
            this.logger.error('Failed to load user for deletion:', error);
            this.showNotification('Failed to load user data', 'error');
        }
    }

    /**
     * Submit delete user request
     * @param {string} userId - User ID
     */
    async submitDeleteUser(userId) {
        try {
            const deleteBtn = document.getElementById('confirm-delete-user');
            deleteBtn.disabled = true;
            deleteBtn.innerHTML = '<span class="material-symbols-outlined">hourglass_empty</span> Deleting...';

            await this.api.delete(`${config.apiUrl}/users/${userId}`);

            this.overlay.closeOverlay();
            this.showNotification('User deleted successfully', 'success');
            await this.loadUsersTable();

        } catch (error) {
            this.logger.error('Failed to delete user:', error);
            this.showNotification('Failed to delete user: ' + (error.message || 'Unknown error'), 'error');

            const deleteBtn = document.getElementById('confirm-delete-user');
            if (deleteBtn) {
                deleteBtn.disabled = false;
                deleteBtn.innerHTML = '<span class="material-symbols-outlined">delete</span> Delete User';
            }
        }
    }

    /**
     * Show notification toast
     * @param {string} message - Notification message
     * @param {string} type - Type: 'success', 'error', 'info'
     */
    showNotification(message, type = 'info') {
        const notification = document.createElement('div');
        notification.className = `user-notification user-notification-${type}`;

        const icon = type === 'success' ? 'check_circle' : type === 'error' ? 'error' : 'info';

        notification.innerHTML = `
            <span class="material-symbols-outlined">${icon}</span>
            <span>${message}</span>
        `;

        document.body.appendChild(notification);

        // Trigger animation
        setTimeout(() => notification.classList.add('show'), 10);

        // Auto-dismiss after 3 seconds
        setTimeout(() => {
            notification.classList.remove('show');
            setTimeout(() => notification.remove(), 300);
        }, 3000);
    }

    // ===============================
    // UTILITY METHODS
    // ===============================

    /**
     * Get current user ID from JWT token
     * @returns {string|null} Current user ID
     */
    getCurrentUserId() {
        try {
            const token = localStorage.getItem('accessToken');
            if (!token) return null;
            
            const payload = JSON.parse(atob(token.split('.')[1]));
            return payload.sub || null;
        } catch (error) {
            this.logger.warn('Failed to get current user ID from token:', error);
            return null;
        }
    }

    /**
     * Check if user is current user
     * @param {string} userId - User ID to check
     * @returns {boolean} True if is current user
     */
    isCurrentUser(userId) {
        return userId === this.getCurrentUserId();
    }

    /**
     * Get gender display name (German)
     * @param {string} gender - Gender value
     * @returns {string} Display name
     */
    getGenderDisplayName(gender) {
        return this.manager.getGenderDisplayName({ gender });
    }

    /**
     * Handle unlock user action
     * @param {string} userId - User ID
     * @param {string} email - User email
     */
    async handleUnlockUser(userId, email) {
        this.logger.debug(`Unlocking user ${email} (${userId})`);

        try {
            const unlockBtn = document.querySelector(`.btn-unlock-user[data-user-id="${userId}"]`);
            if (unlockBtn) {
                unlockBtn.disabled = true;
                unlockBtn.innerHTML = '<span class="material-symbols-outlined">hourglass_empty</span>';
            }

            // Call unlock API endpoint
            await this.api.post(`${config.apiUrl}/users/${userId}/unlock`, {});

            this.showNotification(`Account unlocked for ${email}`, 'success');
            this.logger.success(`Account unlocked for ${email}`);

            // Reload the users table to update the UI
            await this.loadUsersTable();

        } catch (error) {
            this.logger.error('Failed to unlock user:', error);
            this.showNotification('Failed to unlock user account', 'error');

            // Re-enable button if it exists
            const unlockBtn = document.querySelector(`.btn-unlock-user[data-user-id="${userId}"]`);
            if (unlockBtn) {
                unlockBtn.disabled = false;
                unlockBtn.innerHTML = '<span class="material-symbols-outlined">lock_open</span>';
            }
        }
    }

    // ===============================
    // BACKWARD COMPATIBILITY METHODS
    // ===============================

    /**
     * Alias for displaying current user profile
     */
    async displayUserProfile() {
        return await this.displayCurrentUserProfile();
    }

    /**
     * Alias for displaying related users  
     */
    async displayUsers() {
        return await this.displayRelatedUsers();
    }
}

const userController = new UserController();
export default userController;